import React, { useEffect, useState, useCallback } from "react";
import AdminLayout from "src/layout/admin";
import GenericDataLayout from "src/components/custom/GenericDataLayout";
import GenericTable from "src/components/custom/GenericTable";
import Modal from "src/components/modal";
import Button from "src/components/custom/Button";
import TrainerFilterSelect from "src/components/mcc/TrainerFilterSelect";
import TrainerFormFields from "src/components/mcc/TrainerFormFields";
import {
  getMethodApiCall,
  postMethodApiCall,
  putMethodApiCall,
} from "src/services/api";
import { toast } from "react-toastify";
import {
  emptyAddForm,
  emptyEditForm,
  mapTrainerDetailToForm,
  validateTrainerMccForm,
} from "src/utils/mcc/trainerHelper";

const DetailRow = ({ label, value }) => (
  <div className="grid grid-cols-1 sm:grid-cols-[10rem_1fr] gap-1 sm:gap-3 border-b border-gray-100 py-2.5 last:border-0">
    <span className="text-xs font-semibold text-primary">{label}</span>
    <span className="text-sm text-gray-800 break-words">{value ?? "—"}</span>
  </div>
);

const mapTradesByItiResponseToOptions = (data) =>
  (data || [])
    .filter((row) => row?.trade_name && row?.id != null)
    .map((row) => ({
      label: row.trade_name,
      value: Number(row.id),
    }));

const TABLE_COLUMNS = [
  { header: "PHONE", accessor: "phone_number" },
  { header: "PASSWORD", accessor: "password" },
  { header: "NAME", accessor: "name" },
  { header: "TRADE", accessor: "trade" },
  { header: "ITI", accessor: "iti" },
];

const Trainer = () => {
  const [tableRows, setTableRows] = useState([]);
  const [listLoading, setListLoading] = useState(false);
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 10,
    totalRecords: 0,
  });

  const [addModalOpen, setAddModalOpen] = useState(false);
  const [addForm, setAddForm] = useState(() => emptyAddForm());
  const [createSubmitting, setCreateSubmitting] = useState(false);
  const [itiOptions, setItiOptions] = useState([]);
  const [addTradeOptions, setAddTradeOptions] = useState([]);
  const [editTradeOptions, setEditTradeOptions] = useState([]);

  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [detailViewMode, setDetailViewMode] = useState("view");
  const [trainerDetailRaw, setTrainerDetailRaw] = useState(null);
  const [editForm, setEditForm] = useState(() => emptyEditForm());
  const [detailLoading, setDetailLoading] = useState(false);
  const [editSubmitting, setEditSubmitting] = useState(false);

  const [filterPhone, setFilterPhone] = useState("");
  const [filterName, setFilterName] = useState("");

  const loadTrainers = useCallback(
    async (
      page = 1,
      limit = pagination.limit,
      phone = filterPhone,
      name = filterName
    ) => {
      setListLoading(true);
      try {
        const params = { page, limit };
        if (phone) params.phone_number = phone;
        if (name) params.name = name;
        const res = await getMethodApiCall("/v1/api/mcc/trainers", params);
        if (res?.status === 200 && res?.data?.success) {
          setTableRows(res.data.data || []);
          const p = res.data.pagination || {};
          setPagination((prev) => ({
            ...prev,
            page: p.page ?? page,
            limit: p.limit ?? limit,
            totalRecords: p.total ?? 0,
          }));
        } else {
          setTableRows([]);
          toast.error(res?.data?.message || "Failed to load trainers");
        }
      } catch {
        setTableRows([]);
        toast.error("Failed to load trainers");
      } finally {
        setListLoading(false);
      }
    },
    [pagination.limit, filterPhone, filterName]
  );

  useEffect(() => {
    loadTrainers(1, pagination.limit);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!addModalOpen && !detailModalOpen) return;

    const loadItiPicklist = async () => {
      const itiRes = await getMethodApiCall("/v1/api/mcc/itis/picklist");

      if (itiRes?.status === 200 && itiRes?.data?.success) {
        const opts = (itiRes.data.data || [])
          .filter((row) => row?.iti_name && row?.id != null)
          .map((row) => {
            const d = row.district ? String(row.district).trim() : "";
            const label = d ? `${row.iti_name} — ${d}` : String(row.iti_name);
            return { label, value: Number(row.id) };
          });
        setItiOptions(opts);
      } else {
        setItiOptions([]);
        if (addModalOpen) {
          toast.error(itiRes?.data?.message || "Failed to load ITI list");
        }
      }
    };

    loadItiPicklist();
  }, [addModalOpen, detailModalOpen]);

  useEffect(() => {
    if (!addModalOpen) {
      setAddTradeOptions([]);
      return;
    }
    const itiId = addForm.iti_id;
    if (!itiId) {
      setAddTradeOptions([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      const res = await getMethodApiCall(
        `/v1/api/trainee/trades_by_iti/${encodeURIComponent(itiId)}`
      );
      if (cancelled) return;
      if (res?.status === 200 && res?.data?.success) {
        setAddTradeOptions(mapTradesByItiResponseToOptions(res.data.data));
      } else {
        setAddTradeOptions([]);
        toast.error(res?.data?.message || "Failed to load trades for this ITI");
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [addModalOpen, addForm.iti_id]);

  useEffect(() => {
    if (!detailModalOpen || detailViewMode !== "edit") {
      setEditTradeOptions([]);
      return;
    }
    const itiId = editForm.iti_id;
    if (!itiId) {
      setEditTradeOptions([]);
      return;
    }

    let cancelled = false;
    const load = async () => {
      const res = await getMethodApiCall(
        `/v1/api/trainee/trades_by_iti/${encodeURIComponent(itiId)}`
      );
      if (cancelled) return;
      if (res?.status === 200 && res?.data?.success) {
        setEditTradeOptions(mapTradesByItiResponseToOptions(res.data.data));
      } else {
        setEditTradeOptions([]);
        toast.error(res?.data?.message || "Failed to load trades for this ITI");
      }
    };
    load();
    return () => {
      cancelled = true;
    };
  }, [detailModalOpen, detailViewMode, editForm.iti_id]);

  const openAddModal = () => {
    setAddForm(emptyAddForm());
    setAddModalOpen(true);
  };

  const closeAddModal = () => setAddModalOpen(false);

  const clearAddForm = () => {
    setAddForm(emptyAddForm());
  };

  const handleCreateTrainer = async () => {
    const v = validateTrainerMccForm(addForm);
    if (!v.ok) {
      toast.error(v.message);
      return;
    }

    setCreateSubmitting(true);
    try {
      const res = await postMethodApiCall("/v1/api/mcc/trainers", {
        name: addForm.name.trim(),
        phone_number: String(addForm.phone_number).trim(),
        iti_id: Number(addForm.iti_id),
        trade_id: Number(addForm.trade_id),
      });

      if ((res?.status === 200 || res?.status === 201) && res?.data?.success) {
        toast.success(res.data.message || "Trainer created successfully");
        clearAddForm();
        setAddModalOpen(false);
        await loadTrainers(1, pagination.limit, filterPhone, filterName);
      } else {
        toast.error(
          res?.data?.message ||
            res?.data?.error ||
            "Failed to create trainer"
        );
      }
    } catch {
      toast.error("Failed to create trainer");
    } finally {
      setCreateSubmitting(false);
    }
  };

  const closeDetailModal = () => {
    setDetailModalOpen(false);
    setDetailViewMode("view");
    setTrainerDetailRaw(null);
    setEditForm(emptyEditForm());
  };

  const reloadDetailFromServer = async (id) => {
    const res = await getMethodApiCall(`/v1/api/mcc/trainers/${id}`);
    if (res?.status === 200 && res?.data?.success && res.data.data) {
      setTrainerDetailRaw(res.data.data);
      setEditForm(mapTrainerDetailToForm(res.data.data));
      return true;
    }
    toast.error(res?.data?.message || "Failed to load trainer");
    return false;
  };

  const handleRowClick = async (row) => {
    if (!row?.id) return;
    setDetailLoading(true);
    setDetailModalOpen(true);
    setDetailViewMode("view");
    try {
      await reloadDetailFromServer(row.id);
    } finally {
      setDetailLoading(false);
    }
  };

  const handleUpdateTrainer = async () => {
    const v = validateTrainerMccForm(editForm);
    if (!v.ok) {
      toast.error(v.message);
      return;
    }
    if (!editForm.id) {
      toast.error("Missing trainer id");
      return;
    }

    setEditSubmitting(true);
    try {
      const res = await putMethodApiCall(`/v1/api/mcc/trainers/${editForm.id}`, {
        name: editForm.name.trim(),
        phone_number: String(editForm.phone_number).trim(),
        password: String(editForm.password ?? "").trim(),
        iti_id: Number(editForm.iti_id),
        trade_id: Number(editForm.trade_id),
      });

      if (res?.status === 200 && res?.data?.success) {
        toast.success(res.data.message || "Trainer updated successfully");
        await reloadDetailFromServer(editForm.id);
        setDetailViewMode("view");
        await loadTrainers(
          pagination.page,
          pagination.limit,
          filterPhone,
          filterName
        );
      } else {
        toast.error(
          res?.data?.message ||
            res?.data?.error ||
            "Failed to update trainer"
        );
      }
    } catch {
      toast.error("Failed to update trainer");
    } finally {
      setEditSubmitting(false);
    }
  };

  const cancelEditBackToView = async () => {
    if (!editForm.id) {
      closeDetailModal();
      return;
    }
    setDetailLoading(true);
    try {
      await reloadDetailFromServer(editForm.id);
      setDetailViewMode("view");
    } finally {
      setDetailLoading(false);
    }
  };

  const handlePageChange = (newPage) => {
    setPagination((prev) => ({ ...prev, page: newPage }));
    loadTrainers(newPage, pagination.limit, filterPhone, filterName);
  };

  const handleLimitChange = (newLimit) => {
    setPagination((prev) => ({ ...prev, limit: newLimit, page: 1 }));
    loadTrainers(1, newLimit, filterPhone, filterName);
  };

  const handleResetTrainerFilters = () => {
    setFilterPhone("");
    setFilterName("");
    setPagination((prev) => ({ ...prev, page: 1 }));
    loadTrainers(1, pagination.limit, "", "");
  };

  const trainerFilterConfig = [
    {
      type: "custom",
      render: () => (
        <TrainerFilterSelect
          label="Phone number"
          field="phone_number"
          value={filterPhone}
          onChange={(v) => {
            setFilterPhone(v);
            setPagination((p) => ({ ...p, page: 1 }));
            loadTrainers(1, pagination.limit, v, filterName);
          }}
          placeholder="Search phone number…"
        />
      ),
    },
    {
      type: "custom",
      render: () => (
        <TrainerFilterSelect
          label="Name"
          field="name"
          value={filterName}
          onChange={(v) => {
            setFilterName(v);
            setPagination((p) => ({ ...p, page: 1 }));
            loadTrainers(1, pagination.limit, filterPhone, v);
          }}
          placeholder="Search name…"
        />
      ),
    },
  ];

  const actions = [
    {
      label: "Add",
      onClick: (e) => {
        e.preventDefault?.();
        openAddModal();
      },
    },
  ];

  const detailTitle =
    detailViewMode === "view" ? "Trainer details" : "Edit trainer";

  return (
    <AdminLayout>
      <GenericDataLayout
        title="Trainer"
        subtitle=""
        actions={actions}
        filters={trainerFilterConfig}
        onResetFilters={handleResetTrainerFilters}
        isLoading={listLoading}
        pagination={{
          page: pagination.page,
          limit: pagination.limit,
          totalRecords: pagination.totalRecords,
          onPageChange: handlePageChange,
          onLimitChange: handleLimitChange,
        }}
      >
        <GenericTable
          columns={TABLE_COLUMNS}
          data={tableRows}
          pagination={pagination}
          onRowClick={handleRowClick}
        />
      </GenericDataLayout>

      <Modal isOpen={addModalOpen} onClose={closeAddModal} title="Add Trainer">
        <TrainerFormFields
          form={addForm}
          setForm={setAddForm}
          itiOptions={itiOptions}
          tradeOptions={addTradeOptions}
        />
        <div className="mt-6 flex justify-end gap-3">
          <button
            type="button"
            onClick={clearAddForm}
            disabled={createSubmitting}
            className="py-2 px-6 min-w-[8rem] rounded-md border-2 border-primary text-primary font-semibold shadow-sm hover:bg-primary/10 focus:outline-none focus:ring-2 focus:ring-primary focus:ring-offset-2 transition ease-in duration-200 disabled:opacity-50"
          >
            Clear
          </button>
          <Button
            title={createSubmitting ? "Creating..." : "Create"}
            disabled={createSubmitting}
            onClick={handleCreateTrainer}
          />
        </div>
      </Modal>

      <Modal
        isOpen={detailModalOpen}
        onClose={closeDetailModal}
        title={detailTitle}
      >
        {detailLoading && (
          <p className="text-sm text-gray-500 py-4">Loading…</p>
        )}

        {!detailLoading && trainerDetailRaw && detailViewMode === "view" && (
          <>
            <div className="space-y-0 pr-1">
              <DetailRow label="Phone number" value={trainerDetailRaw.phone_number} />
              <DetailRow label="Password" value={trainerDetailRaw.password} />
              <DetailRow label="Name" value={trainerDetailRaw.name} />
              <DetailRow label="ITI" value={trainerDetailRaw.iti_name} />
              <DetailRow label="District" value={trainerDetailRaw.district} />
              <DetailRow label="Trade" value={trainerDetailRaw.trade_name} />
            </div>
            <div className="mt-6 flex justify-end gap-3">
              <button
                type="button"
                onClick={closeDetailModal}
                className="py-2 px-6 min-w-[8rem] rounded-md border-2 border-gray-300 text-gray-700 font-semibold shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition ease-in duration-200"
              >
                Cancel
              </button>
              <Button title="Edit" onClick={() => setDetailViewMode("edit")} />
            </div>
          </>
        )}

        {!detailLoading && trainerDetailRaw && detailViewMode === "edit" && (
          <>
            <TrainerFormFields
              form={editForm}
              setForm={setEditForm}
              itiOptions={itiOptions}
              tradeOptions={editTradeOptions}
              showPassword
            />
            <div className="mt-6 flex flex-wrap justify-end gap-3">
              <button
                type="button"
                onClick={cancelEditBackToView}
                disabled={editSubmitting}
                className="py-2 px-6 min-w-[8rem] rounded-md border-2 border-gray-300 text-gray-700 font-semibold shadow-sm hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-gray-400 focus:ring-offset-2 transition ease-in duration-200 disabled:opacity-50"
              >
                Cancel
              </button>
              <Button
                title={editSubmitting ? "Updating..." : "Update"}
                disabled={editSubmitting}
                onClick={handleUpdateTrainer}
              />
            </div>
          </>
        )}
      </Modal>
    </AdminLayout>
  );
};

export default Trainer;
